"use client";

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { AuthChangeEvent } from '@supabase/supabase-js';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from './AuthContext';
import { authSessionLoad } from '@/lib/perf/authSessionLoad';

// ── Types ─────────────────────────────────────────────────────

export type CapstoneProject = {
  id: string;
  projectNumber: number;
  productionName: string | null;
  logline: string | null;
  synopsis: string | null;
  shootingDays: number;
  loadOutDate: string;
  checkoutDate: string;
  checkinDate: string;
  checkoutTime: string;
  checkinTime: string;
  preliminaryReminderDate: string;
  hardLockDate: string;
  status: string;
};

export type CapstoneCrew = {
  id: string;
  projectId: string;
  role: string;
  department: string;
  studentId: string;
  candidateRank: string;
  status: string;
  invitedBy: string;
  invitedAt: string;
  respondedAt: string | null;
};

export type CapstoneDelegate = {
  id: string;
  projectId: string;
  crewId: string;
  delegateId: string;
  department: string;
  status: string;
};

export type CapstoneRequest = {
  id: string;
  projectId: string;
  stage: string;
  status: string;
  version: number;
  activityLog: any[];
  submittedAt: string | null;
  submittedBy: string | null;
};

export type CapstoneRequestItem = {
  id: string;
  requestId: string;
  department: string;
  barcode: string;
  itemName: string;
  qty: number;
  addedBy: string;
  status: string;
  deptApprovalFrom: string | null;
  deptResponseAt: string | null;
  staffNote: string | null;
};

export type CapstoneNotification = {
  id: string;
  projectId: string;
  type: string;
  read: boolean;
  data: any;
  createdAt: string;
};

export type CapstoneStaffNote = {
  id: string;
  requestId: string;
  level: string;
  barcode: string | null;
  department: string | null;
  note: string;
  authorId: string;
  createdAt: string;
};

// ── Crew roles config ─────────────────────────────────────────

export const CREW_ROLES = [
  { role: 'Director',                    department: 'View_Only',   label: 'Director'                         },
  { role: 'DP',                          department: 'Camera',      label: 'Director of Photography'          },
  { role: 'Gaffer',                      department: 'Electric',    label: 'Gaffer'                           },
  { role: 'Key_Grip',                    department: 'Grip',        label: 'Key Grip'                         },
  { role: 'Sound',                       department: 'Sound',       label: 'Sound Mixer'                      },
  { role: 'Production_Coordinator',      department: 'Production',  label: 'Production Coordinator / Office PA' },
];

// Colors sourced from direction-e-aperture design spec
export const DEPT_COLORS: Record<string, string> = {
  Camera:     'bg-violet-100 text-violet-700 dark:bg-violet-950/50 dark:text-violet-300',
  Electric:   'bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-300',
  Grip:       'bg-orange-100 text-orange-700 dark:bg-orange-950/50 dark:text-orange-300',
  Sound:      'bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300',
  Production: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300',
  View_Only:  'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
  All:        'bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300',
};

// ── Context ───────────────────────────────────────────────────

interface CapstoneContextValue {
  enabled: boolean;
  loading: boolean;
  // Current user's capstone state (for their "primary" project)
  myCrew: CapstoneCrew | null;
  myProject: CapstoneProject | null;
  projectCrew: CapstoneCrew[];
  projectDelegates: CapstoneDelegate[];
  myRequest: CapstoneRequest | null;
  requestItems: CapstoneRequestItem[];
  staffNotes: CapstoneStaffNote[];
  notifications: CapstoneNotification[];
  // All projects (always populated for the job board)
  availableProjects: CapstoneProject[];
  // Map of projectId → accepted producer crew entry (job board display)
  producerMap: Record<string, CapstoneCrew>;
  // Map of projectId → this user's crew entry (for multi-project awareness)
  myCrewByProjectId: Record<string, CapstoneCrew>;
  // All projects where this user is an active delegate (even without crew membership)
  myDelegateProjects: CapstoneDelegate[];
  // Derived helpers
  isProducer: boolean;
  myDepartment: string;
  refresh: () => void;
  // Switch the active project — loads that project's crew/request/items into context.
  // Call refresh() to restore the primary project when navigating back.
  switchProject: (projectId: string) => Promise<void>;
}

const CapstoneContext = createContext<CapstoneContextValue | null>(null);

let _supabase: ReturnType<typeof createClient> | null = null;
function getSupabase() {
  if (!_supabase) _supabase = createClient();
  return _supabase;
}

function toProject(row: any): CapstoneProject {
  return {
    id:                      row.id,
    projectNumber:           row.project_number,
    productionName:          row.production_name ?? null,
    logline:                 row.logline ?? null,
    synopsis:                row.synopsis ?? null,
    shootingDays:            row.shooting_days,
    loadOutDate:             row.load_out_date,
    checkoutDate:            row.checkout_date,
    checkinDate:             row.checkin_date,
    checkoutTime:            row.checkout_time,
    checkinTime:             row.checkin_time,
    preliminaryReminderDate: row.preliminary_reminder_date,
    hardLockDate:            row.hard_lock_date,
    status:                  row.status,
  };
}

function toCrew(row: any): CapstoneCrew {
  return {
    id:            row.id,
    projectId:     row.project_id,
    role:          row.role,
    department:    row.department,
    studentId:     row.student_id,
    candidateRank: row.candidate_rank,
    status:        row.status,
    invitedBy:     row.invited_by,
    invitedAt:     row.invited_at,
    respondedAt:   row.responded_at ?? null,
  };
}

function toDelegate(row: any): CapstoneDelegate {
  return {
    id:         row.id,
    projectId:  row.project_id,
    crewId:     row.crew_id,
    delegateId: row.delegate_id,
    department: row.department,
    status:     row.status,
  };
}

function toRequest(row: any): CapstoneRequest {
  return {
    id:          row.id,
    projectId:   row.project_id,
    stage:       row.stage,
    status:      row.status,
    version:     row.version,
    activityLog: row.activity_log ?? [],
    submittedAt: row.submitted_at ?? null,
    submittedBy: row.submitted_by ?? null,
  };
}

function toItem(row: any): CapstoneRequestItem {
  return {
    id:               row.id,
    requestId:        row.request_id,
    department:       row.department,
    barcode:          row.barcode,
    itemName:         row.item_name,
    qty:              row.qty,
    addedBy:          row.added_by,
    status:           row.status,
    deptApprovalFrom: row.dept_approval_from ?? null,
    deptResponseAt:   row.dept_response_at ?? null,
    staffNote:        row.staff_note ?? null,
  };
}

function toNote(row: any): CapstoneStaffNote {
  return {
    id:         row.id,
    requestId:  row.request_id,
    level:      row.level,
    barcode:    row.barcode ?? null,
    department: row.department ?? null,
    note:       row.note,
    authorId:   row.author_id,
    createdAt:  row.created_at,
  };
}

export function CapstoneProvider({ children }: { children: React.ReactNode }) {
  const supabase = getSupabase();
  const { user } = useAuth();

  const [enabled, setEnabled]                     = useState(false);
  const [loading, setLoading]                     = useState(true);
  const [myCrew, setMyCrew]                       = useState<CapstoneCrew | null>(null);
  const [myProject, setMyProject]                 = useState<CapstoneProject | null>(null);
  const [projectCrew, setProjectCrew]             = useState<CapstoneCrew[]>([]);
  const [projectDelegates, setProjectDelegates]   = useState<CapstoneDelegate[]>([]);
  const [myRequest, setMyRequest]                 = useState<CapstoneRequest | null>(null);
  const [requestItems, setRequestItems]           = useState<CapstoneRequestItem[]>([]);
  const [staffNotes, setStaffNotes]               = useState<CapstoneStaffNote[]>([]);
  const [notifications, setNotifications]         = useState<CapstoneNotification[]>([]);
  const [availableProjects, setAvailableProjects]       = useState<CapstoneProject[]>([]);
  const [producerMap, setProducerMap]                   = useState<Record<string, CapstoneCrew>>({});
  const [myCrewByProjectId, setMyCrewByProjectId]       = useState<Record<string, CapstoneCrew>>({});
  const [myDelegateProjects, setMyDelegateProjects]     = useState<CapstoneDelegate[]>([]);

  const userId = (user as any)?.id as string | undefined;

  const fetchAll = useCallback(async () => {
    const uid = userId;
    if (!uid) { setLoading(false); return; }
    setLoading(true);

    // Wave 1: none of these depend on each other's results — run concurrently
    // instead of one after another (this used to be up to 12 sequential
    // round trips per fetch; batching by dependency wave cuts that to 3 max).
    const [
      { data: settings },
      { data: crewRows },
      { data: allProjs },
      { data: producers },
      { data: myDelegateRows },
      { data: notifs },
    ] = await Promise.all([
      supabase.from('capstone_settings').select('enabled').single(),
      // All active crew entries for this user (across all projects)
      supabase
        .from('capstone_crew')
        .select('*')
        .eq('student_id', uid)
        .not('status', 'in', '("declined","disregarded")')
        .order('invited_at', { ascending: false }),
      // Always fetch all projects for the job board
      supabase.from('capstone_projects').select('*').order('project_number'),
      // Always fetch accepted producers for the job board display
      supabase.from('capstone_crew').select('*').eq('role', 'Producer').eq('status', 'accepted'),
      // Check if this user is an active delegate on any project (even without crew membership)
      supabase.from('capstone_delegates').select('*').eq('delegate_id', uid).eq('status', 'active'),
      // Notifications (unread)
      supabase
        .from('capstone_notifications')
        .select('*')
        .eq('recipient_id', uid)
        .eq('read', false)
        .order('created_at', { ascending: false }),
    ]);

    setEnabled(settings?.enabled ?? false);

    // Build map of all the user's crew entries by project
    const crewByProjMap: Record<string, CapstoneCrew> = {};
    (crewRows ?? []).forEach((r: any) => { crewByProjMap[r.project_id] = toCrew(r); });
    setMyCrewByProjectId(crewByProjMap);

    // Primary crew entry: prefer accepted Producer, else most recent accepted, else most recent
    const accepted = (crewRows ?? []).filter((r: any) => r.status === 'accepted');
    const primary = accepted.find((r: any) => r.role === 'Producer') ?? accepted[0] ?? crewRows?.[0] ?? null;
    const crew = primary ? toCrew(primary) : null;
    setMyCrew(crew);

    setAvailableProjects((allProjs ?? []).map(toProject));

    const pMap: Record<string, CapstoneCrew> = {};
    (producers ?? []).forEach((r: any) => { pMap[r.project_id] = toCrew(r); });
    setProducerMap(pMap);

    setMyDelegateProjects((myDelegateRows ?? []).map(toDelegate));

    setNotifications(notifs?.map((n: any) => ({
      id: n.id, projectId: n.project_id, type: n.type,
      read: n.read, data: n.data, createdAt: n.created_at,
    })) ?? []);

    if (crew) {
      // Wave 2: all scoped to crew.projectId, none depends on another's result.
      const [
        { data: projRow },
        { data: allCrew },
        { data: delegates },
        { data: reqRow },
      ] = await Promise.all([
        supabase.from('capstone_projects').select('*').eq('id', crew.projectId).single(),
        supabase.from('capstone_crew').select('*').eq('project_id', crew.projectId),
        supabase.from('capstone_delegates').select('*').eq('project_id', crew.projectId),
        supabase.from('capstone_requests').select('*').eq('project_id', crew.projectId).single(),
      ]);

      setMyProject(projRow ? toProject(projRow) : null);
      setProjectCrew((allCrew ?? []).map(toCrew));
      setProjectDelegates((delegates ?? []).map(toDelegate));

      const req = reqRow ? toRequest(reqRow) : null;
      setMyRequest(req);

      if (req) {
        // Wave 3: both scoped to req.id, independent of each other.
        const [{ data: items }, { data: notes }] = await Promise.all([
          supabase.from('capstone_request_items').select('*').eq('request_id', req.id).order('department').order('created_at'),
          supabase.from('capstone_staff_notes').select('*').eq('request_id', req.id).order('created_at'),
        ]);
        setRequestItems((items ?? []).map(toItem));
        setStaffNotes((notes ?? []).map(toNote));
      } else {
        setRequestItems([]);
        setStaffNotes([]);
      }
    } else {
      setMyProject(null);
      setProjectCrew([]);
      setProjectDelegates([]);
      setMyRequest(null);
      setRequestItems([]);
      setStaffNotes([]);
    }

    setLoading(false);
  }, [userId]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event: AuthChangeEvent, session) => {
      const decision = authSessionLoad(event, !!session?.user);
      if (decision === 'load') fetchAll();
      if (decision === 'clear') { setEnabled(false); setMyCrew(null); setMyProject(null); setLoading(false); }
    });
    return () => subscription.unsubscribe();
  }, [fetchAll]);

  const myDepartment = myCrew?.department ?? '';
  const isProducer = myCrew?.role === 'Producer' && myCrew?.status === 'accepted';

  // Load project-specific data for any project the user navigates to,
  // even if it isn't their "primary" crew entry project.
  const switchProject = useCallback(async (projectId: string) => {
    const crewEntry = myCrewByProjectId[projectId];
    const isDelegateProject = myDelegateProjects.some(d => d.projectId === projectId);
    if (!crewEntry && !isDelegateProject) return;

    // Point myCrew at this project's crew entry (if crew); delegates keep myCrew null
    if (crewEntry) setMyCrew(crewEntry);

    const [
      { data: projRow },
      { data: allCrew },
      { data: delegates },
      { data: reqRow },
    ] = await Promise.all([
      supabase.from('capstone_projects').select('*').eq('id', projectId).single(),
      supabase.from('capstone_crew').select('*').eq('project_id', projectId),
      supabase.from('capstone_delegates').select('*').eq('project_id', projectId),
      supabase.from('capstone_requests').select('*').eq('project_id', projectId).single(),
    ]);

    setMyProject(projRow ? toProject(projRow) : null);
    setProjectCrew((allCrew ?? []).map(toCrew));
    setProjectDelegates((delegates ?? []).map(toDelegate));

    const req = reqRow ? toRequest(reqRow) : null;
    setMyRequest(req);

    if (req) {
      const [{ data: items }, { data: notes }] = await Promise.all([
        supabase.from('capstone_request_items').select('*').eq('request_id', req.id).order('department').order('created_at'),
        supabase.from('capstone_staff_notes').select('*').eq('request_id', req.id).order('created_at'),
      ]);
      setRequestItems((items ?? []).map(toItem));
      setStaffNotes((notes ?? []).map(toNote));
    } else {
      setRequestItems([]);
      setStaffNotes([]);
    }
  }, [myCrewByProjectId, myDelegateProjects]);

  return (
    <CapstoneContext.Provider value={{
      enabled, loading,
      myCrew, myProject, projectCrew, projectDelegates,
      myRequest, requestItems, staffNotes, notifications,
      availableProjects, producerMap, myCrewByProjectId,
      myDelegateProjects,
      isProducer,
      myDepartment,
      refresh: fetchAll,
      switchProject,
    }}>
      {children}
    </CapstoneContext.Provider>
  );
}

export function useCapstone() {
  const ctx = useContext(CapstoneContext);
  if (!ctx) throw new Error('useCapstone must be used within CapstoneProvider');
  return ctx;
}
